import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { Form, FormTemplate } from '@nrwl/rx-models';
import { Observable, Subject, fromEvent, merge } from 'rxjs';

import { Widget } from './form-widget.model';
import { WidgetActionData } from './widget-action-data';
import { debounceTime } from 'rxjs/operators';
import { ScribeSuggestionMarkerTarget } from '../form-element/scribe-suggestion-marker.model';
import {
  clearScribeSuggestionMarker,
  isScribeSuggestionMarked,
} from '../form-element/scribe-suggestion-marker.util';

export enum TemplateMode {
  MobileProvider,
  MobileKiosk,
  Web,
  RxPortal,
  Printing,
  Custom,
}
@Component({
  template: '',
})
export class WidgetBase implements OnDestroy {
  _payload;
  public set payload(value: Widget) {
    this._payload = value;
  }
  public get payload() {
    return this._payload;
  }

  _entity;
  public set entity(value: Form | FormTemplate) {
    this._entity = value;
  }
  public get entity() {
    return this._entity;
  }

  _parentId: any;
  public set parentId(value: string) {
    this._parentId = value;
  }
  public get parentId() {
    return this._parentId;
  }

  _patientId: any;
  public set patientId(value: any) {
    this._patientId = value;
  }
  public get patientId() {
    return this._patientId;
  }

  _patient: any;
  public set patient(value: any) {
    this._patient = value;
  }
  public get patient() {
    return this._patient;
  }

  _appointmentId: string | number | null;
  public set appointmentId(value: string | number | null) {
    this._appointmentId = value;
  }
  public get appointmentId() {
    return this._appointmentId;
  }

  _appointment: any;
  public set appointment(value: any) {
    this._appointment = value;
  }
  public get appointment() {
    return this._appointment;
  }

  action = new EventEmitter<WidgetActionData>();
  ready = new Subject<boolean>();
  printPreparation = false;

  public _selected = false;
  public set selected(value: boolean) {
    this._selected = value;
    this._changeDetectorRef.detectChanges();
  }
  public get selected() {
    return this._selected;
  }

  public _wrap = false;
  @Input()
  public set wrap(value: boolean) {
    this._wrap = value;
    this._changeDetectorRef.detectChanges();
  }
  public get wrap() {
    return this._wrap;
  }

  public _wrapContent = false;
  @Input()
  public set wrapContent(value: boolean) {
    this._wrapContent = value;
  }
  public get wrapContent() {
    return this._wrapContent;
  }

  public _isDesign = false;
  @Input()
  public set isDesign(value: boolean) {
    this._isDesign = value;
    // FormElementComponent sets design mode before the payload, and templates cannot render
    // without it, so the re-render waits until the payload is there.
    if (this._payload) {
      this._changeDetectorRef.detectChanges();
    }
  }
  public get isDesign() {
    return this._isDesign;
  }

  // Kiosk
  public _mode = '';
  public set mode(value: string) {
    this._mode = value;
    this._changeDetectorRef.detectChanges();
  }
  public get mode() {
    return this._mode;
  }

  public _isPreview = false;
  public set isPreview(value: boolean) {
    this._isPreview = value;
    this._changeDetectorRef.detectChanges();
  }
  public get isPreview() {
    return this._isPreview;
  }

  public _isPrinting = false;
  public set isPrinting(value: boolean) {
    this._isPrinting = value;
    this._changeDetectorRef.detectChanges();
  }
  public get isPrinting() {
    return this._isPrinting;
  }

  public _isSubmitted = false;
  public set isSubmitted(value: boolean) {
    this._isSubmitted = value;
    this._changeDetectorRef.detectChanges();
  }
  public get isSubmitted() {
    return this._isSubmitted;
  }

  public _isPristine = false;
  public set isPristine(value: boolean) {
    this._isPristine = value;
    this._changeDetectorRef.detectChanges();
  }
  public get isPristine() {
    return this._isPristine;
  }

  public isRxPortal = false;

  public _isEmrReadonly = false;
  @Input()
  public set isEmrReadonly(value: boolean) {
    this._isEmrReadonly = value;

    // `isEmrReadonly` can be assigned before `payload` during dynamic widget setup.
    // Avoid forcing an early render pass while `payload` is still undefined.
    if (this._payload !== undefined) {
      this._changeDetectorRef.detectChanges();
    }
  }
  public get isEmrReadonly() {
    return this._isEmrReadonly;
  }

  private _ngZone;
  private _changeDetectorRef;

  private DEBOUNCE_TIME = 100;

  constructor(private injectorObj: Injector) {
    this._ngZone = this.injectorObj.get(NgZone);
    this._changeDetectorRef = this.injectorObj.get(ChangeDetectorRef);
    this.initListener();
  }

  public initListener() {
    this._ngZone.runOutsideAngular(() => {
      const touchEndSource = fromEvent(document, 'touchend');
      const clickSource = fromEvent(document, 'click');
      const keyboardSource = fromEvent(document, 'keyup');

      merge(touchEndSource, clickSource, keyboardSource)
        .pipe(debounceTime(this.DEBOUNCE_TIME))
        .subscribe((response) => {
          if (this.selected) {
            this._changeDetectorRef.detectChanges();
          }
        });
    });
  }

  isValid() {
    return true;
  }

  public isScribeSuggestionMarked(
    target: ScribeSuggestionMarkerTarget | null | undefined,
    fieldKey?: string,
  ): boolean {
    return isScribeSuggestionMarked(target, fieldKey);
  }

  public clearScribeSuggestionMarker(
    target: ScribeSuggestionMarkerTarget | null | undefined,
    fieldKey?: string,
  ): void {
    if (!target) {
      return;
    }

    clearScribeSuggestionMarker(target, fieldKey);
    this._changeDetectorRef.markForCheck();
  }

  ngOnDestroy() {}
}
